import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, MessageCircle, CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { useLanguage } from '../context/LanguageContext';
import { Reveal } from '../hooks/useReveal';
import { SectionTitle } from './About';
import { contactInfo } from '../mock';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', type: '', place: '', size: '', message: '' });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        event_type: form.type,
        event_date: date ? format(date, 'PPP') : '',
        event_place: form.place,
        event_size: form.size,
        message: form.message,
      });
      toast({ description: t.contact.success });
      setForm({ name: '', email: '', phone: '', type: '', place: '', size: '', message: '' });
      setDate(null);
    } catch (err) {
      toast({ description: 'Error / Intenta de nuevo', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const infoItems = [
    { icon: Phone, label: contactInfo.phone, href: `tel:${contactInfo.phoneRaw}` },
    { icon: Mail, label: contactInfo.email, href: `mailto:${contactInfo.email}` },
    { icon: MessageCircle, label: 'WhatsApp', href: contactInfo.whatsapp },
    { icon: MapPin, label: contactInfo.city, href: contactInfo.maps },
  ];

  return (
    <section id="contact" className="section-pad bg-[#14120d]">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <SectionTitle eyebrow={t.contact.eyebrow} title={t.contact.title} subtitle={t.contact.subtitle} />
        </Reveal>

        <div className="grid lg:grid-cols-5 gap-10 mt-14">
          <Reveal className="lg:col-span-3">
            <form onSubmit={submit} className="card-lux p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="font-sans-el text-[13px] tracking-[0.12em] uppercase text-[#c2bbaa]">{t.contact.form.name}</Label>
                  <Input required value={form.name} onChange={onChange('name')} className="bg-[#111] border-[#e6b83f]/25 focus-visible:ring-[#e6b83f]/40 text-[#f7f1e3]" />
                </div>
                <div className="space-y-2">
                  <Label className="font-sans-el text-[13px] tracking-[0.12em] uppercase text-[#c2bbaa]">{t.contact.form.email}</Label>
                  <Input type="email" required value={form.email} onChange={onChange('email')} className="bg-[#111] border-[#e6b83f]/25 focus-visible:ring-[#e6b83f]/40 text-[#f7f1e3]" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="font-sans-el text-[13px] tracking-[0.12em] uppercase text-[#c2bbaa]">{t.contact.form.phone}</Label>
                  <Input value={form.phone} onChange={onChange('phone')} className="bg-[#111] border-[#e6b83f]/25 focus-visible:ring-[#e6b83f]/40 text-[#f7f1e3]" />
                </div>
                <div className="space-y-2">
                  <Label className="font-sans-el text-[13px] tracking-[0.12em] uppercase text-[#c2bbaa]">{t.contact.form.type}</Label>
                  <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                    <SelectTrigger className="bg-[#111] border-[#e6b83f]/25 text-[#f7f1e3]">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141210] border-[#e6b83f]/25 text-[#f7f1e3]">
                      {t.contact.form.types.map((ty) => (
                        <SelectItem key={ty} value={ty} className="focus:bg-[#e6b83f]/15 focus:text-[#f5deA0]">{ty}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="font-sans-el text-[13px] tracking-[0.12em] uppercase text-[#c2bbaa]">{t.contact.form.date}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="w-full flex items-center gap-2 h-10 px-3 rounded-md bg-[#111] border border-[#e6b83f]/25 text-left text-[#f7f1e3] text-sm hover:border-[#e6b83f]/50 transition-colors">
                        <CalendarDays size={16} className="text-[#e6b83f]" />
                        {date ? format(date, 'PPP') : <span className="text-[#a19a88]">{t.contact.form.pickDate}</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#1a1710] border-[#e6b83f]/25" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} initialFocus className="text-[#f7f1e3]" />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="font-sans-el text-[13px] tracking-[0.12em] uppercase text-[#c2bbaa]">{t.contact.form.size}</Label>
                  <Select value={form.size} onValueChange={(v) => setForm((f) => ({ ...f, size: v }))}>
                    <SelectTrigger className="bg-[#111] border-[#e6b83f]/25 text-[#f7f1e3]">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1710] border-[#e6b83f]/25 text-[#f7f1e3]">
                      {t.contact.form.sizes.map((sz) => (
                        <SelectItem key={sz} value={sz} className="focus:bg-[#e6b83f]/15 focus:text-[#f5deA0]">{sz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-sans-el text-[13px] tracking-[0.12em] uppercase text-[#c2bbaa]">{t.contact.form.place}</Label>
                <Input value={form.place} onChange={onChange('place')} className="bg-[#111] border-[#e6b83f]/25 focus-visible:ring-[#e6b83f]/40 text-[#f7f1e3]" />
              </div>

              <div className="space-y-2">
                <Label className="font-sans-el text-[13px] tracking-[0.12em] uppercase text-[#c2bbaa]">{t.contact.form.message}</Label>
                <Textarea rows={4} value={form.message} onChange={onChange('message')} className="bg-[#111] border-[#e6b83f]/25 focus-visible:ring-[#e6b83f]/40 text-[#f7f1e3] resize-none" />
              </div>

              <button type="submit" disabled={loading} className="btn-gold w-full py-4 rounded-sm text-[14px] tracking-[0.2em] uppercase font-medium disabled:opacity-60">
                {loading ? '…' : t.contact.form.submit}
              </button>
            </form>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-2">
            <div className="card-lux p-8 h-full flex flex-col">
              <h3 className="font-deco text-xl text-[#f7f1e3] mb-6">{t.contact.infoTitle}</h3>
              <div className="space-y-5">
                {infoItems.map((it) => (
                  <a key={it.label} href={it.href} target="_blank" rel="noreferrer" className="flex items-center gap-4 group">
                    <span className="w-11 h-11 rounded-sm border border-[#e6b83f]/35 flex items-center justify-center group-hover:bg-[#e6b83f]/10 transition-colors shrink-0">
                      <it.icon size={18} className="text-[#f5deA0]" />
                    </span>
                    <span className="font-sans-el text-[15px] text-[#cfc8b8] group-hover:text-[#f5deA0] transition-colors break-all">{it.label}</span>
                  </a>
                ))}
              </div>

              <div className="mt-auto pt-8 flex flex-col gap-3">
                <a href={`tel:${contactInfo.phoneRaw}`} className="btn-ghost-gold py-3 rounded-sm text-[13px] tracking-[0.2em] uppercase text-center inline-flex items-center justify-center gap-2">
                  <Phone size={15} /> {t.contact.quickCall}
                </a>
                <a href={contactInfo.whatsapp} target="_blank" rel="noreferrer" className="btn-gold py-3 rounded-sm text-[13px] tracking-[0.2em] uppercase text-center inline-flex items-center justify-center gap-2">
                  <MessageCircle size={15} /> {t.contact.quickWa}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
